import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LoadingGrid, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";
import { payWithPayU } from "@/lib/payu";

export default function Plans() {
  const [plans, setPlans] = useState(null);
  const [current, setCurrent] = useState(null);
  const [hrmsCurrent, setHrmsCurrent] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busyId, setBusyId] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmCancelHrms, setConfirmCancelHrms] = useState(false);
  const [downgradeFor, setDowngradeFor] = useState(null);
  const [busyAction, setBusyAction] = useState(false);

  const load = () => {
    api.get("/public/plans").then(({ data }) => setPlans(data.plans)).catch(() => setPlans([]));
    api.get("/billing/subscription").then(({ data }) => {
      setCurrent(data.subscription);
      setHrmsCurrent(data.hrms_subscription);
    }).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const cancel = async (kind = "hiring") => {
    setBusyAction(true);
    try {
      const { data } = await api.post(`/billing/subscription/cancel?kind=${kind}`);
      toast.success(`Subscription cancelled — active until ${new Date(data.active_until).toLocaleDateString("en-IN")}`);
      setConfirmCancel(false);
      setConfirmCancelHrms(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyAction(false);
    }
  };

  const reactivate = async (kind = "hiring") => {
    setBusyAction(true);
    try {
      await api.post(`/billing/subscription/reactivate?kind=${kind}`);
      toast.success("Subscription reactivated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyAction(false);
    }
  };

  const scheduleDowngrade = async (planId) => {
    setBusyAction(true);
    try {
      const { data } = await api.post("/billing/subscription/schedule", { plan_id: planId, cycle, kind: "hiring" });
      toast.success(`Switch to ${data.scheduled_plan} scheduled for ${new Date(data.effective_at).toLocaleDateString("en-IN")}`);
      setDowngradeFor(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyAction(false);
    }
  };

  const subscribe = async (plan) => {
    if (plan.custom) {
      toast.info("Enterprise is custom priced — our team will reach out. Write to sales@jobspring.example");
      return;
    }
    setBusyId(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "subscription", ref_id: plan.id, cycle, origin_url: window.location.origin,
      });
      payWithPayU(data.payu_url, data.params);
    } catch (e) {
      toast.error(errMsg(e));
      setBusyId(null);
    }
  };

  if (!plans) return <LoadingGrid count={4} />;
  const hiringPlans = plans.filter((p) => (p.kind || "hiring") === "hiring");
  const hrmsPlans = plans.filter((p) => p.kind === "hrms");

  const renderPlan = (p, currentSub) => {
    const price = cycle === "monthly" ? p.price_monthly : p.price_yearly;
    const isCurrent = currentSub?.plan_id === p.id;
    const cap = p.kind === "hrms" ? `${p.max_employees} employees` : `${p.max_active_jobs} active jobs`;
    return (
      <div
        key={p.id}
        className={`bg-white rounded-2xl p-7 border flex flex-col card-hover ${p.highlight ? "glow-border border-[#F5A623]" : "border-border/60"}`}
        data-testid={`plan-${p.id}`}
      >
        <h3 className="font-heading font-bold text-xl text-[#0B1B3D]">{p.name}</h3>
        <p className="mt-3">
          {p.custom ? (
            <span className="font-heading font-extrabold text-3xl">Custom</span>
          ) : (
            <>
              <span className="font-heading font-extrabold text-3xl text-[#0B1B3D]">{inr(price)}</span>
              <span className="text-sm text-muted-foreground">/{cycle === "monthly" ? "mo" : "yr"}</span>
            </>
          )}
        </p>
        <p className="text-xs text-muted-foreground mt-1">+ GST · {cap}</p>
        <ul className="mt-5 space-y-2 flex-1">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <Button
          onClick={() => subscribe(p)}
          disabled={busyId === p.id || isCurrent}
          className={`mt-6 rounded-full w-full ${isCurrent ? "bg-emerald-600" : p.highlight ? "bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-bold" : "bg-[#0B1B3D]"}`}
          data-testid={`subscribe-${p.id}`}
        >
          {isCurrent ? "Current Plan" : busyId === p.id ? "Redirecting…" : p.custom ? "Talk to Sales" : currentSub ? "Switch Plan" : "Subscribe"}
        </Button>
      </div>
    );
  };

  return (
    <div data-testid="plans-page">
      {current && (
        <div className={`rounded-xl p-5 mb-8 flex flex-wrap items-center justify-between gap-4 border ${
          current.status === "active" ? "bg-emerald-50 border-emerald-200" :
          current.status === "grace_period" ? "bg-amber-50 border-amber-300" : "bg-red-50 border-red-200"
        }`} data-testid="current-plan-banner">
          <div>
            <p className="font-heading font-semibold text-[#0B1B3D]">
              {current.plan_name} ({current.cycle}) — <span className="capitalize">{current.status.replace(/_/g, " ")}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {current.status === "cancelled" ? "Cancels at period end:" : "Renews"} {fmtDate(current.expires_at)} · {current.max_active_jobs} active jobs included
              {current.scheduled_plan_id && (
                <span className="text-[#2563EB] font-semibold"> · Switching to {plans?.find((p) => p.id === current.scheduled_plan_id)?.name} at renewal</span>
              )}
            </p>
            {current.status === "grace_period" && (
              <p className="text-xs text-amber-700 font-semibold mt-1">Grace period — pay the due invoice to keep posting jobs.</p>
            )}
          </div>
          {current.status === "active" && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full"
                onClick={() => setDowngradeFor(true)}
                data-testid="downgrade-btn">
                Change Plan
              </Button>
              <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setConfirmCancel(true)} data-testid="cancel-sub-btn">
                Cancel
              </Button>
            </div>
          )}
          {current.status === "cancelled" && (
            <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="reactivate-sub-btn"
              onClick={() => reactivate("hiring")}>
              {busyAction ? "Reactivating…" : "Reactivate Plan"}
            </Button>
          )}
        </div>
      )}

      {hrmsCurrent && (
        <div className={`rounded-xl p-5 mb-8 flex flex-wrap items-center justify-between gap-4 border ${
          hrmsCurrent.status === "active" ? "bg-blue-50 border-blue-200" :
          hrmsCurrent.status === "grace_period" ? "bg-amber-50 border-amber-300" : "bg-red-50 border-red-200"
        }`} data-testid="current-hrms-banner">
          <div>
            <p className="font-heading font-semibold text-[#0B1B3D]">
              {hrmsCurrent.plan_name} ({hrmsCurrent.cycle}) — <span className="capitalize">{hrmsCurrent.status.replace(/_/g, " ")}</span>
              <span className="text-xs font-normal text-muted-foreground ml-2">HRMS — billed separately from hiring</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {hrmsCurrent.status === "cancelled" ? "Cancels at period end:" : "Renews"} {fmtDate(hrmsCurrent.expires_at)} · up to {hrmsCurrent.max_employees} employees
            </p>
          </div>
          {hrmsCurrent.status === "active" && (
            <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setConfirmCancelHrms(true)} data-testid="cancel-hrms-btn">
              Cancel HRMS
            </Button>
          )}
          {hrmsCurrent.status === "cancelled" && (
            <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="reactivate-hrms-btn"
              onClick={() => reactivate("hrms")}>
              {busyAction ? "Reactivating…" : "Reactivate HRMS"}
            </Button>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={() => setCycle("monthly")}
          data-testid="cycle-monthly"
          className={`text-sm font-semibold rounded-full px-5 py-2 border transition-colors ${cycle === "monthly" ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white border-border"}`}
        >
          Monthly
        </button>
        <button
          onClick={() => setCycle("yearly")}
          data-testid="cycle-yearly"
          className={`text-sm font-semibold rounded-full px-5 py-2 border transition-colors ${cycle === "yearly" ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white border-border"}`}
        >
          Annual <span className="text-[#F5A623]">save ~17%</span>
        </button>
      </div>

      <h2 className="font-heading font-semibold text-lg text-[#0B1B3D] mb-4" data-testid="hiring-plans-heading">Hiring plans — job posting & ATS</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {hiringPlans.map((p) => renderPlan(p, current))}
      </div>

      <h2 className="font-heading font-semibold text-lg text-[#0B1B3D] mt-12 mb-1" data-testid="hrms-plans-heading">HRMS plans — billed separately</h2>
      <p className="text-xs text-muted-foreground mb-4">People & HRMS (attendance, leave, payroll, performance, assets, exit) requires an HRMS plan, independent of hiring.</p>
      <div className="grid md:grid-cols-2 gap-6">
        {hrmsPlans.map((p) => renderPlan(p, hrmsCurrent))}
      </div>
      <p className="text-xs text-muted-foreground mt-8">
        Prices are platform-configured and include plan changes, renewal, grace period and cancellation handling. Invoices are generated automatically with GST.
      </p>

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent data-testid="cancel-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Cancel subscription?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Your {current?.plan_name} plan remains active until {fmtDate(current?.expires_at)}, followed by a grace period. Job posting stops after that.
          </p>
          <Button onClick={() => cancel("hiring")} disabled={busyAction} className="w-full rounded-full bg-red-600 hover:bg-red-700" data-testid="cancel-confirm-btn">
            {busyAction ? "Cancelling…" : "Confirm Cancellation"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmCancelHrms} onOpenChange={setConfirmCancelHrms}>
        <DialogContent data-testid="cancel-hrms-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Cancel HRMS subscription?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Your {hrmsCurrent?.plan_name} plan remains active until {fmtDate(hrmsCurrent?.expires_at)}, followed by a grace period. People & HRMS features stop after that. Hiring plans are unaffected.
          </p>
          <Button onClick={() => cancel("hrms")} disabled={busyAction} className="w-full rounded-full bg-red-600 hover:bg-red-700" data-testid="cancel-hrms-confirm-btn">
            {busyAction ? "Cancelling…" : "Confirm HRMS Cancellation"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!downgradeFor} onOpenChange={() => setDowngradeFor(false)}>
        <DialogContent data-testid="change-plan-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Change plan at renewal</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            The new plan takes effect at the end of the current period ({fmtDate(current?.expires_at)}). Job limits of the new plan apply then.
          </p>
          <div className="space-y-2 mt-2">
            {(plans || []).filter((p) => !p.custom && p.id !== current?.plan_id && (p.kind || "hiring") === "hiring").map((p) => (
              <button
                key={p.id}
                onClick={() => scheduleDowngrade(p.id)}
                disabled={busyAction}
                className="w-full text-left border border-border rounded-xl p-4 hover:border-[#2563EB] transition-colors"
                data-testid={`switch-to-${p.id}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-heading font-semibold">{p.name}</p>
                  <p className="font-bold text-sm">{inr(cycle === "monthly" ? p.price_monthly : p.price_yearly)}/{cycle === "monthly" ? "mo" : "yr"}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{p.max_active_jobs} active jobs</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
